import { Box } from "@mui/material";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import { useScrollFrame } from "../siteKit/motion/scrollFrame";
import { ManualChapterList } from "./ManualChapterList";
import type {
  ManualChapterText,
  ManualTopicText,
} from "./ManualChapterSection";
import type { ManualChapterId } from "./manualChapters";
import { jumpToManualSection, scrollToManualTop } from "./manualJump";
import { MANUAL_STYLES } from "./manualStyles";
import { MANUAL_CHAPTERS } from "./manualTopics";
import { ChevronIcon, ListIcon } from "./TocIcons";
import { UpArrowIcon } from "./UpArrowIcon";

/** 막대가 보이는 범위(화면 높이 비율): 첫 장 위끝이 이보다 위로, 마지막 장 아래끝이 이보다 아래에 있을 때 */
const SHOW_TOP = 0.55;
const SHOW_BOTTOM = 0.45;
const FIRST_CHAPTER = MANUAL_CHAPTERS[0].id;
const LAST_CHAPTER = MANUAL_CHAPTERS[MANUAL_CHAPTERS.length - 1].id;

const chapterOfTopic = (topicId: string | null): ManualChapterId | null =>
  MANUAL_CHAPTERS.find((chapter) =>
    chapter.topics.some((topic) => topic.id === topicId)
  )?.id ?? null;

export interface ManualTocBarProps {
  chapters: Readonly<Record<ManualChapterId, ManualChapterText>>;
  topics: Readonly<Record<string, ManualTopicText>>;
  /** 지금 읽는 절 — 페이지 관찰자가 채운다. 스크롤마다 바뀌므로 펼칠 때 한 번만 읽는다 */
  currentTopicRef: RefObject<string | null>;
}

/**
 * 좁은 화면(900px 이하) 아래 막대(service-manual 「매뉴얼 장 구성」): 「전체 목차」·「처음으로」 를 화면 폭 가득.
 * 장들이 화면에 있을 때만 보이고(스크롤 프레임마다 DOM 속성으로만), 「전체 목차」 는 막대 위로 여덟 장과 지금 장의 절을 펼친다.
 * 닫기는 머리말 모바일 메뉴와 같다 — Esc(초점은 단추로) 또는 막대 밖 누름.
 */
export function ManualTocBar({
  chapters,
  topics,
  currentTopicRef,
}: ManualTocBarProps) {
  const { t } = useTranslation("landing");
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState<string | null>(null);

  useScrollFrame((_, viewportHeight) => {
    const nav = navRef.current;
    // 넓은 화면에서는 막대가 없다(display:none) — 위치를 읽지 않는다
    if (!nav?.getClientRects().length) return;
    const first = document.getElementById(FIRST_CHAPTER);
    const last = document.getElementById(LAST_CHAPTER);
    if (!first || !last) return;
    nav.toggleAttribute(
      "data-on",
      first.getBoundingClientRect().top < viewportHeight * SHOW_TOP &&
        last.getBoundingClientRect().bottom > viewportHeight * SHOW_BOTTOM
    );
  });

  useEffect(() => {
    if (!open) return undefined;
    // 뒤쪽 장을 읽다 펼치면 지금 자리가 목록 아래로 숨으므로, 지금 절(없으면 지금 장)을 목록 가운데로 굴린다
    const list = document.getElementById(listId);
    const current = list?.querySelectorAll<HTMLElement>('[aria-current="true"]');
    const target = current?.[current.length - 1];
    if (list && target) {
      list.scrollTop =
        target.offsetTop - list.clientHeight / 2 + target.offsetHeight / 2;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    const handleClick = (event: globalThis.MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClick);
    };
  }, [open, listId]);

  const toggle = () => {
    if (!open) setCurrentTopic(currentTopicRef.current);
    setOpen(!open);
  };
  const jump = (id: string) => {
    setOpen(false);
    jumpToManualSection(id);
  };
  const backToStart = (event: MouseEvent) => {
    event.preventDefault();
    setOpen(false);
    scrollToManualTop();
  };

  return (
    <Box
      component="nav"
      ref={navRef}
      aria-label={t("site.manual.jumpLabel")}
      sx={MANUAL_STYLES.tocBar}
    >
      <Box sx={MANUAL_STYLES.tocBarRow}>
        <Box
          component="button"
          ref={toggleRef}
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={toggle}
          sx={MANUAL_STYLES.tocBarToggle}
        >
          <ListIcon />
          {t("site.manual.allChapters")}
          <ChevronIcon />
        </Box>
        <Box
          component="a"
          href="#main"
          onClick={backToStart}
          sx={MANUAL_STYLES.tocBarTop}
        >
          <UpArrowIcon />
          {t("site.manual.backToStart")}
        </Box>
      </Box>
      <ManualChapterList
        id={listId}
        hidden={!open}
        variant="bar"
        chapters={chapters}
        currentChapter={chapterOfTopic(currentTopic)}
        topics={{ texts: topics, current: currentTopic }}
        onJump={jump}
      />
    </Box>
  );
}
